// Marshall White detail-page parser. Their JSON-LD @type=RealEstateListing
// is clean (address subobject, numberOfBedrooms, numberOfBathroomsTotal,
// category="Sold") but doesn't include offers.price. The sold price lives in
// the meta description tag:
//   <meta name="description" content="3 bedroom Townhouse sold at 2/25 Talbot
//   Avenue, Balwyn, 3103. Sold for 1700000.00. Peaceful and Private Parkside
//   Oasis"/>
// This parser combines both signals to recover the fields Marshall White's
// page omits from offers/price (today only 16.4% of MW snapshots have
// salePrice; the LLM extractor walks past these scraps).

interface RealEstateListing {
  "@type"?: string | string[];
  name?: string;
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
  };
  numberOfBedrooms?: number;
  numberOfBathroomsTotal?: number;
  category?: string;
  description?: string;
}

function findRealEstateListing(html: string): RealEstateListing | null {
  const blocks = html.matchAll(
    /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi,
  );
  for (const match of blocks) {
    const raw = match[1].trim();
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      const candidates = Array.isArray(parsed) ? parsed : [parsed];
      for (const candidate of candidates) {
        if (!candidate || typeof candidate !== "object") continue;
        const type = (candidate as RealEstateListing)["@type"];
        const typeStr = Array.isArray(type) ? type.join(" ") : type;
        if (typeof typeStr === "string" && /RealEstateListing/i.test(typeStr)) {
          return candidate as RealEstateListing;
        }
      }
    } catch {
      // ignore malformed block, try next
    }
  }
  return null;
}

function readMetaContent(html: string, name: string): string | null {
  const re = new RegExp(
    `<meta[^>]*(?:name|property)=["']${name}["'][^>]*content=["']([^"']+)["']`,
    "i",
  );
  const reAlt = new RegExp(
    `<meta[^>]*content=["']([^"']+)["'][^>]*(?:name|property)=["']${name}["']`,
    "i",
  );
  const m = html.match(re) ?? html.match(reAlt);
  return m ? m[1] : null;
}

export function parseMarshallWhiteDetailHtml(
  html: string,
  _url: string,
): Record<string, unknown> {
  const hit: Record<string, unknown> = {};

  const listing = findRealEstateListing(html);
  if (listing) {
    if (listing.name) hit.address = listing.name;
    if (listing.address?.addressLocality) hit.suburb = listing.address.addressLocality;
    if (listing.address?.addressRegion) hit.state = listing.address.addressRegion;
    if (listing.address?.postalCode) hit.postcode = listing.address.postalCode;
    if (typeof listing.numberOfBedrooms === "number") hit.bedrooms = listing.numberOfBedrooms;
    if (typeof listing.numberOfBathroomsTotal === "number") {
      hit.bathrooms = listing.numberOfBathroomsTotal;
    }
    if (typeof listing.category === "string" && /sold/i.test(listing.category)) {
      hit.listingType = "sold";
    }
  }

  // The meta description carries the sold price and the property type.
  // Format observed in production:
  //   "<n> bedroom <Type> sold at <address>. Sold for <price>.00. <blurb>"
  // Where <Type> is "Townhouse" | "House" | "Apartment" | "Unit" | etc.
  const description = readMetaContent(html, "description");
  if (description) {
    const priceMatch = description.match(/Sold for\s+\$?\s*([\d,]+(?:\.\d+)?)/i);
    if (priceMatch) {
      const numeric = Number(priceMatch[1].replace(/,/g, ""));
      if (Number.isFinite(numeric) && numeric >= 1000) {
        hit.salePrice = numeric;
        hit.priceDisplay = `$${Math.round(numeric).toLocaleString("en-AU")}`;
      }
    }
    const typeMatch = description.match(/\d+\s+(?:bedroom|bed)\s+([A-Za-z]+)\s+sold/i);
    if (typeMatch && !hit.propertyType) {
      hit.propertyType = typeMatch[1];
    }
  }

  return hit;
}
